<script setup lang="ts">
import { computed } from "vue";
import type { TreeNodeDto } from "~/api";

const props = withDefaults(
  defineProps<{
    rootNodes: TreeNodeDto[];
    modelValue: string[];
    disabled?: boolean;
    /** Start the tree from a specific node's children instead of the root (by name). */
    rootNodeName?: string;
  }>(),
  {
    disabled: false,
    rootNodeName: undefined,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string[]): void;
}>();

interface SubHeading {
  guid: string;
  name: string;
  leaves: TreeNodeDto[];
  leavesExclusive: boolean;
}
interface Container {
  key: string;
  heading: string | null;
  leaves: TreeNodeDto[];
  leavesExclusive: boolean;
  subHeadings: SubHeading[];
}

const isSelected = (nodeGuid: string): boolean =>
  props.modelValue.includes(nodeGuid);

const selectedAmongLeaves = (leaves: TreeNodeDto[]): string => {
  const guids = new Set(leaves.map((l) => l.guid));
  return props.modelValue.find((g) => guids.has(g)) ?? "";
};

const findNodeByName = (
  nodes: TreeNodeDto[],
  name: string,
): TreeNodeDto | undefined => {
  for (const node of nodes) {
    if (node.name === name) return node;
    const found = findNodeByName(node.children, name);
    if (found) return found;
  }
  return undefined;
};

const effectiveRootNodes = computed(() => {
  if (props.rootNodeName) {
    const target = findNodeByName(props.rootNodes, props.rootNodeName);
    return target ? target.children : props.rootNodes;
  }
  return props.rootNodes;
});

const isFlatMode = computed(
  () => !!props.rootNodeName && effectiveRootNodes.value !== props.rootNodes,
);

const hasSelectableDescendant = (node: TreeNodeDto): boolean =>
  node.children.some((c) => !!c.finderSettings || hasSelectableDescendant(c));

/**
 * Split a set of siblings into directly-selectable leaves and structural
 * sub-headings. A node becomes a sub-heading as soon as it has any FS-carrying
 * descendant — even if the node itself has FinderSettings. This lets a parent
 * carry exclusivity metadata (e.g. Supportive Living marked exclusive) while
 * still delegating the actual widget to its children. Nodes with neither FS
 * nor selectable descendants are pruned from the render.
 */
const partitionSiblings = (siblings: TreeNodeDto[]) => {
  const leaves: TreeNodeDto[] = [];
  const subHeadings: SubHeading[] = [];
  for (const c of siblings) {
    if (hasSelectableDescendant(c)) {
      const innerLeaves = c.children.filter((ch) => !!ch.finderSettings);
      subHeadings.push({
        guid: c.guid,
        name: c.finderSettings?.displayName || c.name,
        leaves: innerLeaves,
        leavesExclusive: innerLeaves.some(
          (l) => l.finderSettings?.isExclusive === true,
        ),
      });
    } else if (c.finderSettings) {
      leaves.push(c);
    }
  }
  return { leaves, subHeadings };
};

const containers = computed<Container[]>(() => {
  if (isFlatMode.value) {
    const { leaves, subHeadings } = partitionSiblings(effectiveRootNodes.value);
    return [
      {
        key: "flat",
        heading: null,
        leaves,
        leavesExclusive: leaves.some(
          (l) => l.finderSettings?.isExclusive === true,
        ),
        subHeadings,
      },
    ];
  }
  return effectiveRootNodes.value.map((group) => {
    const { leaves, subHeadings } = partitionSiblings(group.children);
    return {
      key: group.guid,
      heading: group.name,
      leaves,
      leavesExclusive: leaves.some(
        (l) => l.finderSettings?.isExclusive === true,
      ),
      subHeadings,
    };
  });
});

const hasSelectableContent = computed(() =>
  containers.value.some(
    (c) =>
      c.leaves.length > 0 || c.subHeadings.some((s) => s.leaves.length > 0),
  ),
);

const findPath = (guid: string): TreeNodeDto[] => {
  const walk = (
    nodes: TreeNodeDto[],
    trail: TreeNodeDto[],
  ): TreeNodeDto[] | null => {
    for (const n of nodes) {
      const next = [...trail, n];
      if (n.guid === guid) return next;
      const hit = walk(n.children, next);
      if (hit) return hit;
    }
    return null;
  };
  return walk(effectiveRootNodes.value, []) ?? [];
};

const isExclusiveLevel = (node: TreeNodeDto): boolean =>
  node.finderSettings?.isExclusive === true;

const collectDescendantGuids = (node: TreeNodeDto, acc: Set<string>) => {
  acc.add(node.guid);
  for (const c of node.children) collectDescendantGuids(c, acc);
};

/**
 * An exclusive node X is mutually exclusive with its siblings. For each
 * exclusive X in the tree: if the new selection sits inside subtree(X) we
 * clear its siblings' subtrees; if the new selection sits inside one of X's
 * siblings, we clear subtree(X); otherwise X is unrelated and left alone.
 * This lets exclusivity cross levels (e.g. picking a Supportive Living
 * category also clears the sibling Below Market/Independent selections).
 */
const computeExclusiveClearSet = (nodeGuid: string): Set<string> => {
  const clear = new Set<string>();
  const path = findPath(nodeGuid);
  if (path.length === 0) return clear;
  const pathGuids = new Set(path.map((n) => n.guid));

  const walkLevel = (siblings: TreeNodeDto[]) => {
    for (const x of siblings) {
      if (isExclusiveLevel(x)) {
        const xOnPath = pathGuids.has(x.guid);
        if (xOnPath) {
          for (const sibling of siblings) {
            if (sibling.guid === x.guid) continue;
            collectDescendantGuids(sibling, clear);
          }
        } else if (
          siblings.some((s) => s.guid !== x.guid && pathGuids.has(s.guid))
        ) {
          collectDescendantGuids(x, clear);
        }
      }
      walkLevel(x.children);
    }
  };
  walkLevel(effectiveRootNodes.value);

  return clear;
};

const applySelection = (nodeGuid: string, checked: boolean) => {
  if (props.disabled) return;
  if (!checked) {
    emit(
      "update:modelValue",
      props.modelValue.filter((g) => g !== nodeGuid),
    );
    return;
  }
  const clear = computeExclusiveClearSet(nodeGuid);
  const next = props.modelValue.filter((g) => !clear.has(g) && g !== nodeGuid);
  next.push(nodeGuid);
  emit("update:modelValue", next);
};

const selectExclusive = (nodeGuid: string) => {
  if (!nodeGuid) return;
  applySelection(nodeGuid, true);
};

const toggleSelection = (nodeGuid: string, checked: boolean) => {
  applySelection(nodeGuid, checked);
};
</script>

<template>
  <div class="sector-finder">
    <template v-if="hasSelectableContent">
      <div
        v-for="container in containers"
        :key="container.key"
        class="sector-group"
      >
        <h4 v-if="container.heading" class="sector-group-title">
          {{ container.heading }}
        </h4>

        <div v-if="container.leaves.length > 0" class="sector-categories">
          <template v-if="container.leavesExclusive">
            <el-radio-group
              :model-value="selectedAmongLeaves(container.leaves)"
              :disabled="props.disabled"
              @update:model-value="selectExclusive($event as string)"
            >
              <el-radio
                v-for="leaf in container.leaves"
                :key="leaf.guid"
                :value="leaf.guid"
              >
                {{ leaf.finderSettings?.displayName || leaf.name }}
              </el-radio>
            </el-radio-group>
          </template>
          <template v-else>
            <el-checkbox
              v-for="leaf in container.leaves"
              :key="leaf.guid"
              :model-value="isSelected(leaf.guid)"
              :disabled="props.disabled"
              @update:model-value="
                toggleSelection(leaf.guid, $event as boolean)
              "
            >
              {{ leaf.finderSettings?.displayName || leaf.name }}
            </el-checkbox>
          </template>
        </div>

        <div
          v-for="sub in container.subHeadings"
          :key="sub.guid"
          class="sector-name"
        >
          <h5 class="sector-name-title">{{ sub.name }}</h5>
          <div class="sector-categories">
            <template v-if="sub.leavesExclusive">
              <el-radio-group
                :model-value="selectedAmongLeaves(sub.leaves)"
                :disabled="props.disabled"
                @update:model-value="selectExclusive($event as string)"
              >
                <el-radio
                  v-for="leaf in sub.leaves"
                  :key="leaf.guid"
                  :value="leaf.guid"
                >
                  {{ leaf.finderSettings?.displayName || leaf.name }}
                </el-radio>
              </el-radio-group>
            </template>
            <template v-else>
              <el-checkbox
                v-for="leaf in sub.leaves"
                :key="leaf.guid"
                :model-value="isSelected(leaf.guid)"
                :disabled="props.disabled"
                @update:model-value="
                  toggleSelection(leaf.guid, $event as boolean)
                "
              >
                {{ leaf.finderSettings?.displayName || leaf.name }}
              </el-checkbox>
            </template>
          </div>
        </div>
      </div>
    </template>
    <div v-else class="sector-finder-empty">
      No sector categories configured.
    </div>
  </div>
</template>

<style scoped>
.sector-finder {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sector-group {
  border: 1px solid var(--el-border-color-light);
  border-radius: 8px;
  padding: 16px;
}

.sector-group-title {
  margin: 0 0 12px 0;
  font-size: 15px;
  font-weight: 600;
}

.sector-name {
  margin-left: 12px;
  margin-bottom: 12px;
}

.sector-name:last-child {
  margin-bottom: 0;
}

.sector-name-title {
  margin: 14px 0 8px 0;
  font-size: 16px;
  font-weight: 500;
  color: var(--el-text-color-regular);
}

.sector-categories {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-left: 12px;
}

@media (max-width: 700px) {
  .sector-categories {
    .el-radio-group {
      :deep(.el-radio__label) {
        font-size: 0.7em;
      }
    }
  }
}

.sector-finder-empty {
  color: var(--el-text-color-secondary);
  text-align: center;
  padding: 20px;
}
</style>
